import { useMemo } from 'react';

import { TId } from '@/features/common';
import { usePipelineCategoriesQuery } from '@/features/pipeline';
import { useJobOpeningId, useWorkspaceId } from '@/hooks/router';

import { useAutomationsQuery } from '../queries';
import { EAutomationType } from '../types';

const TEST_RELATED_AUTOMATION_TYPES: EAutomationType[] = [
  EAutomationType.SendTestInviteToMovedCandidate,
  EAutomationType.MoveOverThresholdCandidate,
  EAutomationType.SendRejectionToUnderThresholdCandidate,
  EAutomationType.RejectUnderThresholdCandidate,
];

/** Check if stage has automations or is used as destination stage for other automations */
export const useIsStageUsedInAutomations = (stageId: TId) => {
  const workspaceId = useWorkspaceId();
  const jobOpeningId = useJobOpeningId();

  const { data: isUsed } = useAutomationsQuery<boolean>(
    { workspaceId, jobOpeningId },
    {
      select: (automations) =>
        automations.some(
          (automation) =>
            automation.stageId === stageId ||
            automation.destinationStageId === stageId
        ),
    }
  );

  return isUsed ?? false;
};

/**
 * Check if test stage has automations related to test
 * @param testId null value allows hook to be confitional for pipeline context menu since not all pipelines have tests
 * */
export const useIsTestUsedInAutomations = (testId: TId | null) => {
  const workspaceId = useWorkspaceId();
  const jobOpeningId = useJobOpeningId();
  const { data: stages } = usePipelineCategoriesQuery(
    { jobOpeningId },
    { enabled: !!testId }
  );
  const testStageId = useMemo(
    () => stages?.find((stage) => stage.testId === testId)?.id,
    [stages, testId]
  );

  const { data: isUsed } = useAutomationsQuery<boolean>(
    { workspaceId, jobOpeningId },
    {
      enabled: !!testStageId,
      select: (automations) =>
        automations.some(
          (automation) =>
            automation.stageId === testStageId &&
            TEST_RELATED_AUTOMATION_TYPES.includes(automation.type)
        ),
    }
  );

  return isUsed ?? false;
};
